<template>
	<LayoutFlex
		class="orders-next-shipment"
		flex-direction="row"
		align-items="center"
		padding="var(--size-20) var(--size-24)"
		gap="var(--size-16)"
		background="--background-white"
		corner-radius="var(--size-24)"
	>
		<!-- Static glyphs from src/assets, inlined so they take currentColor. -->
		<span class="orders-next-shipment__icon" aria-hidden="true" v-html="TRUCK_SVG" />
		<LayoutFlex padding="0" gap="var(--size-4)">
			<Text font="--text-14-20-med" :color="KOVA.textSecondary">Est. next shipment</Text>
			<LayoutFlex padding="0" gap="var(--size-4)" flex-direction="row" align-items="center">
				<Text font="--text-18-22-med" color="--text-dark-primary">{{ date ?? 'Not scheduled' }}</Text>
				<span
					v-if="warning"
					class="orders-next-shipment__warning"
					:class="`orders-next-shipment__warning--${warning}`"
					aria-hidden="true"
					v-html="WARNING_SVG"
				/>
			</LayoutFlex>
		</LayoutFlex>
	</LayoutFlex>
</template>

<script setup lang="ts">
import { LayoutFlex, Text } from '@simple-life-app/growth-ui-kova'
import { KOVA } from '@/designTokens'
import type { OrdersListModel } from '@/orders/listModel'
import { TRUCK_SVG, WARNING_SVG } from './orderGlyphs'

/**
 * "Est. next shipment" on the Orders list (Figma 13715:74905; with the warning glyph 13718:100298 / 13868:139507):
 * the truck tile, the day ("Wed 21 Oct") or "Not scheduled". While the renewal card is at Warning or Error the
 * day gets the warning triangle in that tier's colour — orange / rose, as the frames draw it. The glyph is
 * decorative: the renewal card above says the shipment may be delayed.
 */

defineProps<{
	/** The formatted day, or `null` — "Not scheduled". */
	date: string | null
	/** The model's `nextShipmentWarning`: the renewal card's tier at Warning or Error; `null` draws no glyph. */
	warning: OrdersListModel['nextShipmentWarning']
}>()
</script>

<style scoped>
/* Figma: a 48px `background/surface-on-secondary` tile, radius 20, the 20px truck. The DS ships that grey as
   its neutral status surface. */
.orders-next-shipment__icon {
	display: flex;
	flex: none;
	align-items: center;
	justify-content: center;
	width: var(--size-48);
	height: var(--size-48);
	border-radius: var(--size-20);
	background: var(--status-neutral-background);
	color: var(--text-dark-primary);
}

.orders-next-shipment__warning {
	display: flex;
	flex: none;
}

/* Figma: the 18px `icon-warning` after the day — the 20px icon's 18.9px triangle at 18/20, so 17px wide. */
.orders-next-shipment__warning :deep(svg) {
	width: calc(var(--size-16) + 1px);
	height: auto;
}

.orders-next-shipment__warning--warning {
	color: var(--status-warning-fill);
}

.orders-next-shipment__warning--error {
	color: var(--status-error-fill);
}
</style>
